import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PERMISSION_METADATA } from '@heyroot/contracts/tenants/rbac/permission-metadata';
import { PLATFORM_ONLY_PERMISSIONS } from '@heyroot/contracts/tenants/rbac/platform-only-permissions';
import { ROLE_REGISTRY } from '@heyroot/contracts/tenants/rbac/role-registry';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { Checkbox } from '@heyroot/ui/primitives/Checkbox';
import { Input } from '@heyroot/ui/primitives/Input';
import {
  upsertVersionRole,
  putVersionRole,
  deleteVersionRole,
  queryKeys,
  AdminApiError,
  type BusinessTypeVersion,
} from '../../lib/api';

const ALL_PERMISSIONS = '*';

/**
 * Operator mental model for grouping is `apps/backoffice/.../permission-groups.ts`,
 * but apps/admin has no dependency on apps/backoffice (app-to-app import, not
 * sanctioned) -- see task-10-12 decision log. Fallback: group by the
 * namespace prefix of the key (`financials.*`, `pos.*`, ...), which is the
 * same signal `PERMISSION_METADATA[key].module` encodes, just derived from
 * the key itself rather than re-deriving a parallel taxonomy.
 */
function groupFor(key: string): string {
  const prefix = key.split('.')[0] ?? key;
  return prefix.charAt(0).toUpperCase() + prefix.slice(1).replace(/[-_]/g, ' ');
}

// The catalogue this picker offers is the same union
// modules/tenants/domain/business-type-version-fsm.ts builds for its
// UNKNOWN_PERMISSION check -- ROLE_REGISTRY's granted strings ∪
// PERMISSION_METADATA's keys. PERMISSION_METADATA alone is a sidecar
// annotating a SUBSET of permissions that need step-up/sensitivity
// treatment (its own file header says so); treating it as the catalogue is
// the same defect the FSM was widened to fix server-side, and using it
// alone here made that bug live twice. Excluded from the picker AND rejected
// by validateVersion server-side: PLATFORM_ONLY_PERMISSIONS, plus the
// wildcard forms ('*' is offered separately as the pinned owner control, and
// a `ns.*` grant is not a concrete permission to tick). The picker alone is
// not a guarantee -- it stops holding the first time anything other than
// this UI calls the endpoint.
const ALL_KNOWN_PERMISSIONS = new Set<string>([
  ...Object.values(ROLE_REGISTRY).flatMap((role) => role.permissions),
  ...Object.keys(PERMISSION_METADATA),
]);
const SELECTABLE = [...ALL_KNOWN_PERMISSIONS].filter(
  (k) => !PLATFORM_ONLY_PERMISSIONS.has(k) && k !== '*' && !k.endsWith('.*'),
);

const GROUPS = (() => {
  const byGroup = new Map<string, string[]>();
  for (const k of SELECTABLE) {
    const g = groupFor(k);
    byGroup.set(g, [...(byGroup.get(g) ?? []), k]);
  }
  return [...byGroup.entries()]
    .map(([name, keys]) => ({ name, keys: keys.sort() }))
    .sort((a, b) => a.name.localeCompare(b.name));
})();
// Group sizes now track the ROLE_REGISTRY ∪ PERMISSION_METADATA union above,
// not a fixed count -- every group selection count must read against this
// full set, never a search-filtered subset, or the (n/total) readout
// misreports mid-search.
const GROUP_KEYS_BY_NAME = new Map(GROUPS.map((g) => [g.name, g.keys]));
const ALL_GROUP_NAMES = new Set(GROUPS.map((g) => g.name));

function describe(key: string): string {
  return PERMISSION_METADATA[key]?.description ?? key;
}

/**
 * The permission picker -- a tool, not a form (conv-feature-depth). Search
 * over key + description, collapsible groups with per-group select-all/none,
 * a live "N selected" readout, and the selected set as removable chips.
 */
function PermissionPicker({
  selected,
  onChange,
}: {
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [search, setSearch] = useState('');
  // Dozens of groups, hundreds of keys -- all-expanded by default renders
  // every row before the operator touches anything. Start collapsed; search
  // bypasses collapse entirely since a filtered result is already the
  // overview.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set(ALL_GROUP_NAMES));
  const [preOwnerSelection, setPreOwnerSelection] = useState<Set<string> | null>(null);
  const isOwner = selected.has(ALL_PERMISSIONS);
  const searching = search.trim().length > 0;

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return GROUPS;
    return GROUPS.map((g) => ({
      ...g,
      keys: g.keys.filter(
        (k) => k.toLowerCase().includes(q) || describe(k).toLowerCase().includes(q),
      ),
    })).filter((g) => g.keys.length > 0);
  }, [search]);

  function toggleOwner() {
    if (isOwner) {
      // Restore whatever was selected before owner was turned on, rather than
      // dropping to empty -- turning '*' off must not destroy the operator's
      // prior selection.
      onChange(preOwnerSelection ?? new Set());
      setPreOwnerSelection(null);
    } else {
      setPreOwnerSelection(new Set(selected));
      onChange(new Set([ALL_PERMISSIONS]));
    }
  }

  function toggleOne(key: string) {
    const next = new Set(selected);
    next.delete(ALL_PERMISSIONS);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  }

  function toggleGroup(keys: string[], select: boolean) {
    const next = new Set(selected);
    next.delete(ALL_PERMISSIONS);
    for (const k of keys) {
      if (select) next.add(k);
      else next.delete(k);
    }
    onChange(next);
  }

  function toggleCollapsed(name: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  const selectedList = [...selected].sort();

  return (
    <div className="flex flex-col gap-3">
      <label
        className={`flex items-center justify-between gap-2 rounded-control border p-3 ${
          isOwner ? 'border-brand-primary bg-surface-raised' : 'border-border bg-surface'
        }`}
      >
        <span className="flex items-center gap-2">
          <Checkbox checked={isOwner} onChange={toggleOwner} />
          <span className="text-body-sm-emphasis text-primary">All permissions (owner)</span>
        </span>
        <Badge variant="warning">*</Badge>
      </label>

      {isOwner ? (
        <p className="rounded-control bg-info-pale p-3 text-body-sm text-info-text">
          Owner-equivalent: grants everything. The groups below are disabled because nothing else
          can add to this.
        </p>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search permissions by key or description"
              aria-label="Search permissions"
              className="flex-1"
            />
            <Button type="button" size="sm" variant="ghost" onClick={() => setCollapsed(new Set())}>
              Expand all
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setCollapsed(new Set(ALL_GROUP_NAMES))}
            >
              Collapse all
            </Button>
          </div>

          <div className="max-h-96 overflow-y-auto rounded-control border border-border">
            {filteredGroups.map((g) => {
              // Count against the FULL group, never the search-filtered subset
              // (g.keys here), or the readout misreports mid-search.
              const fullKeys = GROUP_KEYS_BY_NAME.get(g.name) ?? g.keys;
              const groupSelectedCount = fullKeys.filter((k) => selected.has(k)).length;
              const isCollapsed = !searching && collapsed.has(g.name);
              return (
                <div key={g.name} className="border-b border-border last:border-b-0">
                  <div className="flex items-center justify-between gap-2 bg-surface-deep p-2">
                    <button
                      type="button"
                      onClick={() => toggleCollapsed(g.name)}
                      className="flex-1 text-left"
                    >
                      <span className="text-body-sm-emphasis text-primary">
                        {isCollapsed ? '▸' : '▾'} {g.name}{' '}
                        <span className="tabular-nums text-muted">
                          ({groupSelectedCount}/{fullKeys.length})
                        </span>
                      </span>
                    </button>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleGroup(g.keys, true)}
                      >
                        All
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleGroup(g.keys, false)}
                      >
                        None
                      </Button>
                    </div>
                  </div>
                  {!isCollapsed && (
                    <ul className="flex flex-col">
                      {g.keys.map((k) => (
                        <li key={k}>
                          <label className="flex cursor-pointer items-start gap-2 px-3 py-2 hover:bg-surface-sunken">
                            <Checkbox
                              className="mt-0.5"
                              checked={selected.has(k)}
                              onChange={() => toggleOne(k)}
                            />
                            <span className="flex flex-col">
                              <span className="text-body-sm-emphasis text-primary">{k}</span>
                              <span className="text-eyebrow text-muted">{describe(k)}</span>
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="flex flex-col gap-2">
        <p className="text-body-sm text-secondary">
          <span className="tabular-nums text-body-sm-emphasis">{selected.size}</span> permission
          {selected.size === 1 ? '' : 's'} selected
        </p>
        {/* Chip area scrolls within its own container -- a role carrying 200
            permission chips must never widen the page body (spec dense-overflow
            state). */}
        <div className="flex max-h-40 flex-wrap gap-1 overflow-y-auto rounded-control border border-border bg-surface p-2">
          {selectedList.length === 0 ? (
            <span className="text-body-sm text-muted">No permissions selected.</span>
          ) : (
            selectedList.map((k) => (
              <Badge key={k} variant={k === ALL_PERMISSIONS ? 'warning' : 'neutral'}>
                {k}
                <button
                  type="button"
                  aria-label={`Remove ${k}`}
                  className="text-muted hover:text-primary"
                  onClick={() => (k === ALL_PERMISSIONS ? toggleOwner() : toggleOne(k))}
                >
                  <span className="ml-1.5">×</span>
                </button>
              </Badge>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function RoleEditor({
  slug,
  versionId,
  name,
  initialPermissions,
  isNew,
  onDone,
}: {
  slug: string;
  versionId: string;
  name: string;
  initialPermissions: string[];
  isNew: boolean;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [roleName, setRoleName] = useState(name);
  const [permissions, setPermissions] = useState<Set<string>>(new Set(initialPermissions));

  const save = useMutation({
    mutationFn: () =>
      isNew
        ? upsertVersionRole(versionId, { name: roleName, permissions: [...permissions] })
        : putVersionRole(versionId, name, [...permissions]),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.businessType(slug) }),
        qc.invalidateQueries({ queryKey: ['business-type-validate'] }),
      ]);
      onDone();
    },
    onError: (err) => {
      if (err instanceof AdminApiError && err.code === 'VERSION_NOT_EDITABLE') {
        void qc.invalidateQueries({ queryKey: queryKeys.businessType(slug) });
      }
    },
  });

  return (
    <div className="flex flex-col gap-3 rounded-control border border-border bg-surface p-4">
      <label className="flex flex-col gap-1.5">
        <span className="text-body-sm-emphasis text-secondary">Role name</span>
        <Input
          value={roleName}
          disabled={!isNew}
          onChange={(e) => setRoleName(e.target.value)}
          className="max-w-64"
        />
      </label>
      <PermissionPicker selected={permissions} onChange={setPermissions} />
      {save.isError &&
        !(save.error instanceof AdminApiError && save.error.code === 'VERSION_NOT_EDITABLE') && (
          <p className="text-body-sm text-danger-text">
            {save.error instanceof Error ? save.error.message : 'Could not save role.'}
          </p>
        )}
      <div className="flex gap-2">
        <Button
          onClick={() => save.mutate()}
          disabled={!roleName || permissions.size === 0 || save.isPending}
          loading={save.isPending}
        >
          Save role
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function RoleTemplateEditor({
  slug,
  version,
  isDraft,
  issues,
}: {
  slug: string;
  version: BusinessTypeVersion;
  isDraft: boolean;
  issues: { code: string; message: string }[];
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const del = useMutation({
    mutationFn: (name: string) => deleteVersionRole(version.id, name),
    onSuccess: async () => {
      setConfirmDelete(null);
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.businessType(slug) }),
        qc.invalidateQueries({ queryKey: ['business-type-validate'] }),
      ]);
    },
    onError: (err) => {
      if (err instanceof AdminApiError && err.code === 'VERSION_NOT_EDITABLE') {
        setConfirmDelete(null);
        void qc.invalidateQueries({ queryKey: queryKeys.businessType(slug) });
      }
    },
  });

  const roleIssues = issues.filter((i) =>
    [
      'NO_ROLES',
      'ROLE_WITHOUT_PERMISSIONS',
      'DUPLICATE_ROLE',
      'UNKNOWN_PERMISSION',
      'PLATFORM_ONLY_PERMISSION',
    ].includes(i.code),
  );

  if (creating) {
    return (
      <RoleEditor
        slug={slug}
        versionId={version.id}
        name=""
        initialPermissions={[]}
        isNew
        onDone={() => setCreating(false)}
      />
    );
  }

  if (editing) {
    const role = version.roles.find((r) => r.name === editing);
    if (role) {
      return (
        <RoleEditor
          slug={slug}
          versionId={version.id}
          name={role.name}
          initialPermissions={role.permissions}
          isNew={false}
          onDone={() => setEditing(null)}
        />
      );
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {roleIssues.length > 0 && (
        <ul
          role="alert"
          className="flex flex-col gap-1 rounded-control border border-danger bg-danger-pale p-3 text-body-sm text-danger-text"
        >
          {roleIssues.map((i, idx) => (
            <li key={`${i.code}-${idx}`}>{i.message}</li>
          ))}
        </ul>
      )}

      {version.roles.length === 0 ? (
        <p className="text-body-sm text-muted">No role templates on this version yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {version.roles.map((r) => (
            <li
              key={r.name}
              className="flex items-center justify-between gap-3 rounded-control border border-border bg-surface p-3"
            >
              <div>
                <p className="text-body-sm-emphasis text-primary">{r.name}</p>
                <p className="text-eyebrow text-muted">
                  {r.permissions.includes(ALL_PERMISSIONS)
                    ? 'All permissions (owner)'
                    : `${r.permissions.length} permission${r.permissions.length === 1 ? '' : 's'}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={!isDraft}
                  onClick={() => setEditing(r.name)}
                >
                  Edit
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="danger"
                  disabled={!isDraft}
                  onClick={() => setConfirmDelete(r.name)}
                >
                  Delete role
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {isDraft && (
        <Button type="button" variant="secondary" onClick={() => setCreating(true)}>
          Add role
        </Button>
      )}

      {confirmDelete && (
        <div
          role="alertdialog"
          aria-label="Confirm delete role"
          className="flex flex-col gap-3 rounded-control border border-danger bg-danger-pale p-4"
        >
          <p className="text-body-sm text-danger-text">
            Delete role <span className="text-body-sm-emphasis">{confirmDelete}</span>? This cannot
            be undone.
          </p>
          {del.isError &&
            !(del.error instanceof AdminApiError && del.error.code === 'VERSION_NOT_EDITABLE') && (
              <p className="text-body-sm text-danger-text">
                {del.error instanceof Error ? del.error.message : 'Could not delete role.'}
              </p>
            )}
          <div className="flex gap-2">
            <Button
              variant="danger"
              loading={del.isPending}
              onClick={() => del.mutate(confirmDelete)}
            >
              Delete
            </Button>
            <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
